import { useCallback, useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import ChartTooltip from "./ChartTooltip.jsx";
import { useElementWidth } from "../hooks/useElementWidth.js";
import styles from "./ComparisonTrend.module.css";

export default function ComparisonTrend({ profiles, periodDays }) {
  const svgRef = useRef(null);
  const [measureRef, width] = useElementWidth();
  const [tooltip, setTooltip] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  // The same node is both the d3 target and the measured one.
  const setSvg = useCallback((node) => {
    svgRef.current = node;
    measureRef(node);
  }, [measureRef]);

  // The chart is redrawn when the set of profiles or the period changes, and
  // not when a profile still importing reports progress — that arrives every
  // couple of seconds and would restart the line animation each time.
  const signature = `${periodDays}:${profiles.map((profile) => profile.username).join("|")}`;

  useEffect(() => {
    if (!svgRef.current) return;
    const lines = profiles.map((profile) => ({
      label: `@${profile.username}`,
      color: profile.accent.color,
      data: profile.analytics?.weeklySeries || []
    }));
    if (!lines.some((line) => line.data.length > 0)) return;

    const el = svgRef.current;
    const height = 200;
    const margin = { top: 16, right: 8, bottom: 28, left: 32 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;
    // A hidden panel measures 0 and would draw negative geometry. Leave the
    // last good render alone; the observer redraws once it is on screen.
    if (innerW <= 0) return;

    d3.select(el).selectAll("*").remove();

    const svg = d3.select(el)
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("width", "100%")
      .attr("height", height);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const allDates = [...new Set(lines.flatMap((line) => line.data.map((d) => d.date)))].sort();

    const x = d3.scalePoint()
      .domain(allDates)
      .range([0, innerW])
      .padding(0.2);

    const maxCount = Math.max(
      ...lines.map((line) => d3.max(line.data, (d) => d.workoutCount) || 0),
      1
    );
    const y = d3.scaleLinear()
      .domain([0, Math.ceil(maxCount * 1.2)])
      .range([innerH, 0])
      .nice();

    g.append("g")
      .selectAll("line")
      .data(y.ticks(4))
      .join("line")
      .attr("x1", 0).attr("x2", innerW)
      .attr("y1", (d) => y(d)).attr("y2", (d) => y(d))
      .attr("stroke", "rgba(38,33,25,0.08)")
      .attr("stroke-width", 1);

    g.append("g")
      .selectAll("text")
      .data(y.ticks(4))
      .join("text")
      .attr("x", -8).attr("y", (d) => y(d) + 4)
      .attr("text-anchor", "end")
      .attr("fill", "var(--muted)")
      .attr("font-family", "var(--font-sans)")
      .attr("font-size", 11)
      .text((d) => d);

    const drawLine = ({ color, data }, index) => {
      if (!data?.length) return;
      const delay = index * 100;
      const path = g.append("path")
        .datum(data)
        .attr("fill", "none")
        .attr("stroke", color)
        .attr("stroke-width", 2.5)
        .attr("stroke-linecap", "round")
        .attr("d", d3.line()
          .x((d) => x(d.date))
          .y((d) => y(d.workoutCount))
          .curve(d3.curveCatmullRom.alpha(0.5)));
      const len = path.node().getTotalLength();
      path.attr("stroke-dasharray", len).attr("stroke-dashoffset", len)
        .transition().delay(delay).duration(700).ease(d3.easeCubicOut)
        .attr("stroke-dashoffset", 0);

      if (data.length <= 26) {
        g.selectAll(`.dot-${index}`)
          .data(data)
          .join("circle")
          .attr("cx", (d) => x(d.date))
          .attr("cy", (d) => y(d.workoutCount))
          .attr("r", 0)
          .attr("fill", "var(--canvas)")
          .attr("stroke", color)
          .attr("stroke-width", 2.5)
          .transition().delay(delay + 650).duration(200).ease(d3.easeBackOut)
          .attr("r", 3.5);
      }
    };

    lines.forEach(drawLine);

    const step = Math.ceil(allDates.length / 6);
    const labelDates = allDates.filter((_, i) => i === 0 || i === allDates.length - 1 || i % step === 0);
    const labelMap = Object.fromEntries(lines.flatMap((line) => line.data).map((d) => [d.date, d.label]));

    g.selectAll(".xlabel")
      .data(labelDates)
      .join("text")
      .attr("class", "xlabel")
      .attr("x", (d) => x(d))
      .attr("y", innerH + 18)
      .attr("text-anchor", "middle")
      .attr("fill", "var(--muted)")
      .attr("font-family", "var(--font-sans)")
      .attr("font-size", 11)
      .text((d) => labelMap[d] || d);

    const crosshair = g.append("line")
      .attr("y1", 0).attr("y2", innerH)
      .attr("stroke", "rgba(38,33,25,0.30)")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "4,3")
      .attr("opacity", 0)
      .attr("pointer-events", "none");

    g.append("rect")
      .attr("width", innerW).attr("height", innerH)
      .attr("fill", "transparent")
      .attr("cursor", "crosshair")
      .on("mousemove", function(event) {
        const [mx] = d3.pointer(event);
        const idx = d3.minIndex(allDates, (d) => Math.abs(x(d) - mx));
        const date = allDates[idx];
        if (!date) return;
        crosshair.attr("x1", x(date)).attr("x2", x(date)).attr("opacity", 1);
        const rows = lines
          .map(({ label, color, data }) => ({ label, color, count: data.find((d) => d.date === date)?.workoutCount }))
          .filter((row) => row.count != null);
        setTooltip({ label: labelMap[date] || date, rows });
        setTooltipPos({ x: event.clientX, y: event.clientY });
      })
      .on("mouseleave", function() {
        crosshair.attr("opacity", 0);
        setTooltip(null);
      });
  }, [signature, width]);

  const hasData = profiles.some((profile) => profile.analytics?.weeklySeries?.length > 0);

  return (
    <div className={styles.panel}>
      <div className={styles.heading}>
        <span className={styles.label}>Weekly workouts</span>
        <div className={styles.legend}>
          {profiles.map((profile) => (
            <span key={profile.username} className={styles.legendItem} style={{ color: profile.accent.color }}>
              @{profile.username}
            </span>
          ))}
        </div>
      </div>
      {!hasData ? (
        <div className={styles.empty}>No data for this period</div>
      ) : (
        <svg ref={setSvg} className={styles.svg} />
      )}
      {tooltip && (
        <ChartTooltip className={styles.tooltip} x={tooltipPos.x} y={tooltipPos.y}>
          <span className={styles.tooltipDate}>{tooltip.label}</span>
          {tooltip.rows.map((row) => (
            <span key={row.label} className={styles.tooltipRow} style={{ color: row.color }}>
              {row.label}: {row.count}
            </span>
          ))}
        </ChartTooltip>
      )}
    </div>
  );
}
